import { apiClient } from "@apis/apiClient";

import { saveAs } from "file-saver";
import { useEffect, useState } from "react";

/* eslint-disable @typescript-eslint/no-explicit-any */
export default {
  delete(id: any) {
    return apiClient.delete(`/rest/org/${id}`);
  },

  permanentlyDelete(id: any) {
    return apiClient.delete(`/rest/org/${id}/permanentlyDelete`);
  },

  reactivate(id: any) {
    return apiClient.put(`/rest/org/${id}/reactivate`);
  },

  getBasicOrgWithId(id: any) {
    return apiClient.get(`/rest/org/basicOrg/${id}`);
  },

  isAllowConfigARR(id: any) {
    return apiClient.get(`/rest/org/isAllowConfigARR/${id}`);
  },

  getByPartner(id: any) {
    let uri = "/rest/org/partner";
    if (id) {
      uri += "?id=" + id;
    }
    return apiClient.get(uri);
  },

  getBasicInfo() {
    return apiClient.get(`/rest/org/basicOrganisation`);
  },

  getBasicInfoByOrgId(orgId: any) {
    return apiClient.get(`/rest/org/basicOrganisation/forOrgId?orgId=${orgId}`);
  },

  getOrganizationAttributeTypeOptions(organizationAttributeType: any) {
    return apiClient.get(
      `/rest/org/organizationAttributeTypeOptions?organizationAttributeType=${organizationAttributeType}`
    );
  },

  findOne(params: any) {
    return apiClient.get("/rest/org/" + params.id);
  },

  search(params: any) {
    return apiClient.post(`/rest/org/search`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  updateInvoiceSettings(id: any, params: any) {
    return apiClient.put(
      `/rest/org/invoiceSettings/${id}`,
      JSON.stringify(params),
      {
        headers: { "content-type": "application/json" },
      }
    );
  },

  updateBillingEntity(id: any, params: any) {
    return apiClient.put(
      `/rest/org/updateBillingEntity/${id}`,
      JSON.stringify(params),
      {
        headers: { "content-type": "application/json" },
      }
    );
  },

  downloadVerificationFile(orgId: any) {
    return apiClient
      .get("/rest/org/domainVerification/downloadFile/" + orgId, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/octet-stream",
        });
        saveAs(blob, "sensolus-domain-verification-template.html");
      });
  },

  updateSSOButtonsFirst(id: any, params: any) {
    return apiClient.put(`/rest/org/${id}/ssoButtonsFirst`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  updateSSOSetting1(id: any, params: any) {
    return apiClient.put(`/rest/org/${id}/ssoSetting1`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  updateSSOSetting2(id: any, params: any) {
    return apiClient.put(`/rest/org/${id}/ssoSetting2`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  parseIdpMetadataXml(params: any) {
    return apiClient.post(
      `/rest/org/${params.orgId}/parseIdpMetadataXml/${params.ssoIndex}`,
      {
        xmlContent: params.xmlContent,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
  },

  verifyDomain(id: any, domainName: any) {
    return apiClient.post(
      `/rest/org/domainVerification/verify/${id}`,
      domainName,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
  },

  forceVerifyDomain(id: any, domainName: any) {
    return apiClient.post(
      `/rest/org/domainVerification/forceVerify/${id}`,
      domainName,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
  },

  getOrgTagByOrg(orgId: any) {
    return apiClient.get(`/rest/org_tag/getByOrg?orgId=${orgId}`);
  },

  update(id: any, params: any) {
    return apiClient.put(`/rest/org/${id}`, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  create(params: any) {
    return apiClient.post(`/rest/org/`, params, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  getLayers() {
    return apiClient.get(`/rest/org/layers`);
  },

  checkPagePermission(params: any) {
    return apiClient.get(`/rest/org/checkPermission`, { params: params });
  },

  updateBeaconSettings(params: any) {
    return apiClient.put(
      `/rest/org/updateBeaconSettings?orgId=${params.orgId}`,
      params.beaconSettings,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
  },

  getDefaultConfiguration(orgId: any) {
    return apiClient.get(
      `/rest/org/getDefaultConfiguration?orgId=${orgId ? orgId : ""}`
    );
  },

  updateGeozoneVisitBehavior(params: {
    orgId: number;
    geozoneVisitBehavior: string;
    geozoneVisitBehaviorPerGeozone: boolean;
    geozoneVisitStitchTimeoutMinutes?: number | null;
  }) {
    return apiClient.put(
      `/rest/org/updateGeozoneVisitBehavior?orgId=${params.orgId}`,
      {
        geozoneVisitBehavior: params.geozoneVisitBehavior,
        geozoneVisitBehaviorPerGeozone: params.geozoneVisitBehaviorPerGeozone,
        geozoneVisitStitchTimeoutMinutes:
          params.geozoneVisitStitchTimeoutMinutes ?? null,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );
  },

  countOrgsByFilters(params: any) {
    return apiClient.post("/rest/org/filter/count", JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  getOrgByName(orgNames: any) {
    return apiClient.post(`/rest/org/basicOrganisation/byName`, orgNames, {
      headers: {
        "Content-Type": "text/plain",
        Accept: "application/json",
      },
    });
  },

  getSubscriptionConstraints(organisationId: any) {
    return apiClient.get(`/rest/org/subscriptionConstraints/${organisationId}`);
  },

  updateInvoiceReminderSettings(orgId: any, settings: any) {
    return apiClient.put(`/rest/org/${orgId}/invoice-reminder`, settings, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  getAssertionMappings(orgId: number) {
    return apiClient.get(`/rest/org/${orgId}/assertionMappings`);
  },

  getAssertionMappingById(orgId: number, mappingId: number) {
    return apiClient.get(`/rest/org/${orgId}/assertionMappings/${mappingId}`);
  },

  updateAssertionMapping(orgId: number, mappingId: number, data: any) {
    return apiClient.put(`/rest/org/${orgId}/assertionMappings/${mappingId}`, data, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  createAssertionMapping(orgId: number, data: any) {
    return apiClient.post(`/rest/org/${orgId}/assertionMappings`, data, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
  },

  deleteAssertionMapping(orgId: number, mappingId: number) {
    return apiClient.delete(`/rest/org/${orgId}/assertionMappings/${mappingId}`);
  },

  checkReportingBillingEntityMapping(customerId: number, excludeOrgId: number) {
    return apiClient.get(`/rest/org/reporting-billing-entity-mapping/${customerId}`, {
      params: { excludeOrgId },
    });
  },

  getQuotePickerDetail(orgId: number) {
    return apiClient.get(`/rest/org/${orgId}/quotePickerDetail`);
  },

  getPartnerQuotePickerDetail(orgId: number) {
    return apiClient.get(`/rest/org/${orgId}/partnerQuotePickerDetail`);
  },
};

export const useGetOrg = () => {
  const [orgs, setOrgs] = useState<any[]>([]);
  useEffect(() => {
    apiClient.get(`/rest/org/basicOrganisation`).then(({ data }) => {
      const list = [];
      for (let i = 0; i < data.length; i++) {
        list.push({
          value: data[i].id,
          label: data[i].name,
        });
      }
      setOrgs(list);
    });
  }, []);
  return orgs;
};
